//import _ from "lodash";
import React, { useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import withStyles from "@material-ui/core/styles/withStyles";
import loadForm from "../../../core-module/hoc/loadForm";
import config from "../../../config";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { trackPage } from "../../../analytics";
import {
  clearMetaEventId,
  postSignupFormWithMetaConversion,
  trackMetaSignupFormSubmission
} from "../../../metaConversions";

import * as api from "../../../core-module/services/api";

import EasyAutocomplete from "../../../common/components/EasyForm/EasyAutocomplete";
// import EasyAutoSelect from "common/components/EasyForm/EasyAutoSelect";
import EasyRadio from "../../../common/components/EasyForm/EasyRadio";
import EasyOpenInput from "../../../common/components/EasyForm/EasyOpenInput";
import EasyPhoneNumber from "../../../common/components/EasyForm/EasyPhoneNumber";
import EasyErrorMessage from "../../../common/components/EasyForm/EasyErrorMessage";
import SignupRecaptcha from "../../../common/components/SignupRecaptcha/SignupRecaptcha";
import { SignupCard, CtaButton, StepHeader } from "../../../signupComponents";
import Typography from "@material-ui/core/Typography";
import { persistor } from "../../../store";
import SearchResult from "../inside/searchResult";
import filterArea from "../inside/filterArea";

// import ContactInfo from './contactInfo';
import styles from "./styles";

const GOOGLE_MAPS_API_KEY = "AIzaSyAqt4vdJQEPqlyZh4WYGoFOAt3BXIzow9o";
const DEFAULT_MAP_CENTER = { lat: 53.99136, lng: -111.291747 };
const PRAIRIE_LOCATION_BIAS_BOUNDS = {
  south: 49.730618,
  west: -114.586663,
  north: 57.150388,
  east: -104.815975
};
let googleMapsScriptPromise = null;

const isValidCoordinate = (lat, lng) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180 &&
  !(lat === 0 && lng === 0);

const ATS_FIELDS = [
  "ats_quarter",
  "ats_section",
  "ats_township",
  "ats_range",
  "ats_meridian"
];

const getCoordinates = value => {
  const rawValue = value && value.raw ? value.raw : value || {};
  const source = rawValue.object || rawValue;
  const lat = Number(
    source.lat ||
      source.latitude ||
      source.gps_lat ||
      source.gpsLat ||
      source.gpsLatitude
  );
  const lng = Number(
    source.lng ||
      source.lon ||
      source.longitude ||
      source.gps_lng ||
      source.gpsLng ||
      source.gpsLongitude
  );

  if (!isValidCoordinate(lat, lng)) {
    return null;
  }

  return { lat, lng };
};

const loadGoogleMapsScript = () => {
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (googleMapsScriptPromise) {
    return googleMapsScriptPromise;
  }

  googleMapsScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () =>
        resolve(window.google.maps)
      );
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleMapsScriptPromise;
};

const LocationMapPicker = ({
  classes,
  connectForm,
  initialCenter,
  lat,
  lng,
  setLat,
  setLng
}) => {
  const mapRef = React.useRef(null);
  const searchContainerRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const overlayRef = React.useRef(null);
  const connectFormRef = React.useRef(connectForm);
  const setLatRef = React.useRef(setLat);
  const setLngRef = React.useRef(setLng);
  const [mapStatus, setMapStatus] = React.useState("Loading satellite map...");
  const [dragPin, setDragPin] = React.useState(null);
  const [selectedPosition, setSelectedPosition] = React.useState(() => {
    const initialLat = Number(connectForm.getValue("lat") || lat);
    const initialLng = Number(connectForm.getValue("lng") || lng);

    if (isValidCoordinate(initialLat, initialLng)) {
      return { lat: initialLat, lng: initialLng };
    }

    return null;
  });
  const initialPositionRef = React.useRef(selectedPosition);

  React.useEffect(() => {
    connectFormRef.current = connectForm;
    setLatRef.current = setLat;
    setLngRef.current = setLng;
  }, [connectForm, setLat, setLng]);

  React.useEffect(() => {
    const nextLat = Number(connectForm.getValue("lat") || lat);
    const nextLng = Number(connectForm.getValue("lng") || lng);

    if (!isValidCoordinate(nextLat, nextLng)) {
      return;
    }

    const nextPosition = { lat: nextLat, lng: nextLng };
    setSelectedPosition(nextPosition);

    if (markerRef.current) {
      markerRef.current.setPosition(nextPosition);
      markerRef.current.setVisible(true);
    }

    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo(nextPosition);
      if (mapInstanceRef.current.getZoom() < 16) {
        mapInstanceRef.current.setZoom(17);
      }
    }

    setMapStatus("Pin saved. Drag it or click map to adjust.");
  }, [connectForm, lat, lng]);

  const savePosition = React.useCallback(position => {
    const nextLat = Number(position.lat);
    const nextLng = Number(position.lng);

    if (!isValidCoordinate(nextLat, nextLng)) return;

    setSelectedPosition({ lat: nextLat, lng: nextLng });
    connectFormRef.current.changeMany({
      lat: nextLat.toFixed(6),
      lng: nextLng.toFixed(6)
    });
    if (typeof setLatRef.current === "function") setLatRef.current(nextLat);
    if (typeof setLngRef.current === "function") setLngRef.current(nextLng);
    setMapStatus("Pin saved. Drag it or click map to adjust.");
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    loadGoogleMapsScript()
      .then(async maps => {
        if (cancelled || !mapRef.current) return;

        const initialPosition =
          initialPositionRef.current || initialCenter || DEFAULT_MAP_CENTER;
        const hasInitialCenter = Boolean(
          initialPositionRef.current || initialCenter
        );
        const map = new maps.Map(mapRef.current, {
          center: initialPosition,
          zoom: hasInitialCenter ? 17 : 6,
          mapTypeId: "hybrid",
          clickableIcons: false,
          fullscreenControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          zoomControl: true
        });

        const marker = new maps.Marker({
          position: initialPosition,
          map,
          draggable: true,
          visible: Boolean(initialPositionRef.current),
          title: "Service location"
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;
        const overlay = new maps.OverlayView();
        overlay.onAdd = function() {};
        overlay.draw = function() {};
        overlay.onRemove = function() {};
        overlay.setMap(map);
        overlayRef.current = overlay;

        marker.addListener("dragend", () => {
          const markerPosition = marker.getPosition();
          savePosition({
            lat: markerPosition.lat(),
            lng: markerPosition.lng()
          });
        });

        if (searchContainerRef.current && maps.importLibrary) {
          const { PlaceAutocompleteElement } = await maps.importLibrary(
            "places"
          );

          if (cancelled || !searchContainerRef.current) return;

          searchContainerRef.current.innerHTML = "";

          const placeAutocomplete = new PlaceAutocompleteElement({});
          placeAutocomplete.placeholder = "Search city or town";
          placeAutocomplete.includedRegionCodes = ["ca"];
          placeAutocomplete.locationBias = PRAIRIE_LOCATION_BIAS_BOUNDS;
          placeAutocomplete.className = classes.mapSearchAutocomplete;
          searchContainerRef.current.appendChild(placeAutocomplete);

          placeAutocomplete.addEventListener(
            "gmp-select",
            async ({ placePrediction }) => {
              const place = placePrediction.toPlace();
              await place.fetchFields({
                fields: ["displayName", "formattedAddress", "location"]
              });

              if (!place.location) {
                setMapStatus("Place not found. Try another nearby city.");
                return;
              }

              if (place.viewport) {
                map.fitBounds(place.viewport);
              } else {
                map.setCenter(place.location);
                map.setZoom(13);
              }

              setMapStatus("Map moved. Drag pin onto your service location.");
            }
          );
        }

        setMapStatus(
          initialPositionRef.current
            ? "Pin saved. Drag it or click map to adjust."
            : "Drag pin onto map to choose service location."
        );
      })
      .catch(() => {
        if (!cancelled) {
          setMapStatus("Map could not load. Try municipal or legal land.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialCenter, savePosition]);

  const placePinFromClientPoint = clientPoint => {
    const mapElement = mapRef.current;
    const overlay = overlayRef.current;
    const projection = overlay ? overlay.getProjection() : null;

    if (!mapElement || !projection) {
      setMapStatus("Map not ready yet. Try again in a moment.");
      return false;
    }

    const mapRect = mapElement.getBoundingClientRect();
    const isInsideMap =
      clientPoint.clientX >= mapRect.left &&
      clientPoint.clientX <= mapRect.right &&
      clientPoint.clientY >= mapRect.top &&
      clientPoint.clientY <= mapRect.bottom;

    if (!isInsideMap) {
      setMapStatus("Drag pin onto map, then release.");
      return false;
    }

    const latLng = projection.fromContainerPixelToLatLng(
      new window.google.maps.Point(
        clientPoint.clientX - mapRect.left,
        clientPoint.clientY - mapRect.top
      )
    );

    if (!latLng) return false;

    const nextPosition = {
      lat: latLng.lat(),
      lng: latLng.lng()
    };

    if (markerRef.current && mapInstanceRef.current) {
      markerRef.current.setPosition(nextPosition);
      markerRef.current.setVisible(true);
      mapInstanceRef.current.panTo(nextPosition);
      if (mapInstanceRef.current.getZoom() < 16) {
        mapInstanceRef.current.setZoom(17);
      }
    }

    savePosition(nextPosition);
    return true;
  };

  const startPinDrag = event => {
    event.preventDefault();
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    setDragPin({ clientX: event.clientX, clientY: event.clientY });
    setMapStatus("Drag pin onto map, then release.");
  };

  const movePinDrag = event => {
    if (!dragPin) return;
    setDragPin({ clientX: event.clientX, clientY: event.clientY });
  };

  const endPinDrag = event => {
    if (!dragPin) return;
    placePinFromClientPoint({
      clientX: event.clientX,
      clientY: event.clientY
    });
    setDragPin(null);
  };

  return (
    <div className={classes.mapPicker}>
      <div className={classes.mapSearchBar}>
        <div ref={searchContainerRef} className={classes.mapSearchInputWrap} />
      </div>
      <div className={classes.mapStage}>
        <div ref={mapRef} className={classes.mapCanvas} />
        <div className={classes.mapPinShelf}>
          <button
            type="button"
            className={classes.mapPinDragButton}
            onPointerDown={startPinDrag}
            onPointerMove={movePinDrag}
            onPointerUp={endPinDrag}
            onPointerCancel={() => setDragPin(null)}
          >
            <span className={classes.mapPinGlyph}>●</span>
            <span>Drag and Drop this marker</span>
          </button>
        </div>
        {dragPin ? (
          <div
            className={classes.mapFloatingPin}
            style={{
              left: dragPin.clientX,
              top: dragPin.clientY
            }}
          >
            ●
          </div>
        ) : null}
      </div>
      <div className={classes.mapPickerControls}>
        <div>
          <Typography className={classes.mapPickerTitle}>
            {selectedPosition ? "Service pin placed" : "Drop pin on your house"}
          </Typography>
          <Typography className={classes.mapPickerText}>{mapStatus}</Typography>
          {selectedPosition ? (
            <Typography className={classes.mapPickerCoordinates}>
              {selectedPosition.lat.toFixed(6)},{" "}
              {selectedPosition.lng.toFixed(6)}
            </Typography>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const Form = function(props) {
  const [searchq, setSearchq] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const [filteredArea, setFilteredArea] = React.useState([
    { v: 0, l: "", a: "", c: "" }
  ]);
  const [selectedArea, setSelectedArea] = React.useState(
    props.areaId ? { v: props.areaId, a: props.areaName, c: "" } : null
  );
  const [outsideLocationMethod, setOutsideLocationMethod] = React.useState(
    () => sessionStorage.getItem("shortSignupLocationMethod") || "municipal"
  );
  const [insideAddressNeedsGps, setInsideAddressNeedsGps] = React.useState(
    false
  );
  const [insideUsesMapPicker, setInsideUsesMapPicker] = React.useState(false);
  const [
    insideServiceAreaCoordinates,
    setInsideServiceAreaCoordinates
  ] = React.useState(null);
  const [insideAddressWasTried, setInsideAddressWasTried] = React.useState(
    false
  );
  const [recaptchaErrorMessage, setRecaptchaErrorMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const lastAtsCoordinatesQueryRef = React.useRef(null);
  const submissionInFlightRef = useRef(false);
  const isContactStep = props.location.pathname.endsWith("/contact");
  const accountType = props.connectForm.getValue("accountType");
  const gigair = props.connectForm.getValue("gigair");
  const signupClass = props.connectForm.getValue("signupclass");
  const isOutside = props.connectForm.getValue("signupclass") === "outside";
  const connectForm = props.connectForm;
  const setLat = props.setLat;
  const setLng = props.setLng;
  const matchingFirstName = props.connectForm.getValue("firstName1");
  const matchingLastName = props.connectForm.getValue("lastName1");
  const matchingPhone = props.connectForm.getValue("phoneNumber1");

  useEffect(() => {
    trackPage("form");
  }, []);

  useEffect(() => {
    if (props.connectForm.getValue("accountType") === "") {
      props.connectForm.change("p", "accountType");
    }
  }, [props.connectForm]);

  useEffect(() => {
    if (
      props.connectForm.getValue("signupclass") === "" ||
      props.connectForm.getValue("signupclass") == null
    ) {
      props.startOver();
    }
  }, []);

  const stepNum = isContactStep ? 3 : 2;

  const onBackFromStepHeader = () => {
    submissionInFlightRef.current = false;

    if (isContactStep) {
      props.history.push(config.SIGNUP.APP_PATH + "/s/form");
      return;
    }

    persistor.pause();
    persistor.flush().then(() =>
      persistor.purge().then(() => {
        sessionStorage.removeItem("signupformPersistentState");
        sessionStorage.removeItem("shortSignupLocationMethod");
        clearMetaEventId();
        if (props.location.pathname === "/s/form") {
          props.history.push(config.SIGNUP.APP_PATH + "/s/");
        } else {
          window.location.reload();
        }
      })
    );
  };

  useEffect(() => {}, [options]);

  async function updateOptionsState() {
    let res = null;
    const currentAreaId = selectedArea ? selectedArea.v : props.areaId;
    if (props.connectForm.getValue("signupclass") === "inside")
      res = await api.get(
        `guest.1/serviceaddresssearch&searchq=${searchq}&areaid=${currentAreaId}`
      );
    if (props.connectForm.getValue("signupclass") === "outside")
      res = await api.get(`guest.1/ruraladdresssearch&searchq=${searchq}`);
    if (res === null || !res.list) return;
    const newOptions = res.list.map(item => {
      if (item.id == null) {
        return { value: "_NULL", label: item.address, raw: item };
      }
      return { value: item.id, label: item.address, raw: item };
    });
    setOptions(newOptions);
  }

  useEffect(() => {
    if (searchq === null || (searchq.length || 0) < 2) {
      setOptions([]);
      return;
    }
    updateOptionsState();
  }, [searchq]);

  const { classes } = props;

  // const interacted = [];

  const clearForm = () => {
    submissionInFlightRef.current = false;
    persistor.pause();
    persistor.flush().then(() =>
      persistor.purge().then(() => {
        sessionStorage.removeItem("signupformPersistentState");
        sessionStorage.removeItem("shortSignupLocationMethod");
        clearMetaEventId();
        window.location.reload();
      })
    );
  };

  const handleAutoCompleteInputChange = (e, value, reason) => {
    setSearchq(value);
    if (reason === "reset") {
      return;
    }
    if (
      signupClass === "inside" ||
      (isOutside && outsideLocationMethod === "municipal")
    ) {
      setInsideAddressNeedsGps(false);
      props.connectForm.change("", "lat");
      props.connectForm.change("", "lng");
      if (typeof props.setLat === "function") props.setLat(null);
      if (typeof props.setLng === "function") props.setLng(null);
      if (signupClass === "inside") {
        setInsideAddressWasTried(true);
        setInsideUsesMapPicker(false);
      }
    }
  };

  const handleAreaInputChange = event => {
    const value = event.target.value || "";
    if (!value.length) {
      setFilteredArea([{ v: 0, l: "", a: "", c: "" }]);
      return;
    }
    setFilteredArea(filterArea(value, 20));
  };

  const handleSelectArea = value => {
    if (value.v === 0) return;
    setSelectedArea(value);
    props.setAreaid(value.v);
    props.setAreaName(value.a);
    props.connectForm.change(value.v, "areaid");
    props.connectForm.change("", "serviceAddress");
    props.connectForm.change("", "lat");
    props.connectForm.change("", "lng");
    if (typeof props.setLat === "function") props.setLat(null);
    if (typeof props.setLng === "function") props.setLng(null);
    setInsideAddressNeedsGps(false);
    setInsideUsesMapPicker(false);
    setInsideAddressWasTried(false);
    setInsideServiceAreaCoordinates(null);
    setSearchq(null);
    setOptions([]);
  };

  const clearSelectedArea = () => {
    setSelectedArea(null);
    props.setAreaid(null);
    props.setAreaName("");
    props.connectForm.change("", "areaid");
    props.connectForm.change("", "serviceAddress");
    props.connectForm.change("", "lat");
    props.connectForm.change("", "lng");
    if (typeof props.setLat === "function") props.setLat(null);
    if (typeof props.setLng === "function") props.setLng(null);
    setInsideAddressNeedsGps(false);
    setInsideUsesMapPicker(false);
    setInsideAddressWasTried(false);
    setInsideServiceAreaCoordinates(null);
    setFilteredArea([{ v: 0, l: "", a: "", c: "" }]);
    setSearchq(null);
    setOptions([]);
  };

  const showMunicipalAddress =
    !isOutside || outsideLocationMethod === "municipal";
  const showLegalLandDescription =
    isOutside && outsideLocationMethod === "legal";
  const hasValue = propName => {
    const value = props.connectForm.getValue(propName);
    return value != null && String(value).trim() !== "";
  };
  const hasValidValue = propName =>
    hasValue(propName) && props.connectForm.getState(propName) !== "Error";
  const hasBasicInfo =
    hasValidValue("firstName1") &&
    hasValidValue("lastName1") &&
    hasValidValue("phoneNumber1");
  const atsQuarter = props.connectForm.getValue("ats_quarter");
  const atsSection = props.connectForm.getValue("ats_section");
  const atsTownship = props.connectForm.getValue("ats_township");
  const atsRange = props.connectForm.getValue("ats_range");
  const atsMeridian = props.connectForm.getValue("ats_meridian");
  const hasInsideLocation =
    signupClass === "inside" &&
    Boolean(selectedArea) &&
    hasValidValue("serviceAddress") &&
    isValidCoordinate(
      Number(props.connectForm.getValue("lat")),
      Number(props.connectForm.getValue("lng"))
    );
  const hasMunicipalAddress =
    isOutside &&
    outsideLocationMethod === "municipal" &&
    hasValidValue("serviceAddress");
  const hasLegalLandDescriptionFields =
    isOutside &&
    outsideLocationMethod === "legal" &&
    hasValidValue("ats_quarter") &&
    hasValidValue("ats_section") &&
    hasValidValue("ats_township") &&
    hasValidValue("ats_range") &&
    hasValidValue("ats_meridian");
  const hasConfirmedMapLocation = isValidCoordinate(
    Number(props.connectForm.getValue("lat")),
    Number(props.connectForm.getValue("lng"))
  );
  const hasMunicipalLocation = hasMunicipalAddress && hasConfirmedMapLocation;
  const hasLegalLandDescription =
    hasLegalLandDescriptionFields && hasConfirmedMapLocation;
  const hasMapLocation =
    isOutside && outsideLocationMethod === "map" && hasConfirmedMapLocation;
  const hasLocationInfo =
    hasInsideLocation ||
    hasMunicipalLocation ||
    hasLegalLandDescription ||
    hasMapLocation;
  const canSeePricing = hasBasicInfo && hasLocationInfo;
  const insideAddressMissingGps =
    signupClass === "inside" &&
    Boolean(selectedArea) &&
    hasValidValue("serviceAddress") &&
    !hasConfirmedMapLocation;
  const showInsideMapFallback =
    signupClass === "inside" &&
    selectedArea &&
    !insideUsesMapPicker &&
    (insideAddressNeedsGps || insideAddressMissingGps || insideAddressWasTried);
  const showInsideMapLocation =
    signupClass === "inside" &&
    selectedArea &&
    ((hasValidValue("serviceAddress") && hasConfirmedMapLocation) ||
      insideUsesMapPicker);
  const showMapLocation =
    isOutside &&
    (outsideLocationMethod === "map" ||
      (outsideLocationMethod === "municipal" && hasMunicipalAddress) ||
      (outsideLocationMethod === "legal" && hasLegalLandDescriptionFields));

  useEffect(() => {
    if (isContactStep && !hasLocationInfo) {
      props.history.replace(config.SIGNUP.APP_PATH + "/s/form");
    }
  }, [isContactStep, hasLocationInfo, props.history]);

  useEffect(() => {
    let cancelled = false;

    if (!isOutside || outsideLocationMethod !== "legal") {
      lastAtsCoordinatesQueryRef.current = null;
      return undefined;
    }

    if (!hasLegalLandDescriptionFields) {
      lastAtsCoordinatesQueryRef.current = null;
      connectForm.change("", "lat");
      connectForm.change("", "lng");
      if (typeof setLat === "function") setLat(null);
      if (typeof setLng === "function") setLng(null);
      return undefined;
    }

    const query = [
      ["ats_quarter", atsQuarter],
      ["ats_section", atsSection],
      ["ats_township", atsTownship],
      ["ats_range", atsRange],
      ["ats_meridian", atsMeridian]
    ]
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");

    if (lastAtsCoordinatesQueryRef.current === query) {
      return undefined;
    }

    lastAtsCoordinatesQueryRef.current = query;

    api
      .get(`guest.1/atscoordinates&${query}`)
      .then(res => {
        if (cancelled) return;

        const coordinates = getCoordinates(res);
        if (!coordinates) {
          connectForm.change("", "lat");
          connectForm.change("", "lng");
          if (typeof setLat === "function") setLat(null);
          if (typeof setLng === "function") setLng(null);
          return;
        }

        connectForm.changeMany({
          lat: coordinates.lat.toFixed(6),
          lng: coordinates.lng.toFixed(6)
        });
        if (typeof setLat === "function") setLat(coordinates.lat);
        if (typeof setLng === "function") setLng(coordinates.lng);
      })
      .catch(() => {
        if (cancelled) return;

        connectForm.change("", "lat");
        connectForm.change("", "lng");
        if (typeof setLat === "function") setLat(null);
        if (typeof setLng === "function") setLng(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    atsMeridian,
    atsQuarter,
    atsRange,
    atsSection,
    atsTownship,
    hasLegalLandDescriptionFields,
    isOutside,
    outsideLocationMethod,
    connectForm,
    setLat,
    setLng
  ]);

  const chooseOutsideLocationMethod = method => {
    sessionStorage.setItem("shortSignupLocationMethod", method);
    setOutsideLocationMethod(method);
    props.connectForm.change("", "lat");
    props.connectForm.change("", "lng");
    if (typeof props.setLat === "function") props.setLat(null);
    if (typeof props.setLng === "function") props.setLng(null);
    if (method === "municipal") {
      props.connectForm.change("", "ats_quarter");
      props.connectForm.change("", "ats_section");
      props.connectForm.change("", "ats_township");
      props.connectForm.change("", "ats_range");
      props.connectForm.change("", "ats_meridian");
    } else if (method === "legal") {
      props.connectForm.change("", "serviceAddress");
    } else {
      props.connectForm.change("", "serviceAddress");
      props.connectForm.change("", "ats_quarter");
      props.connectForm.change("", "ats_section");
      props.connectForm.change("", "ats_township");
      props.connectForm.change("", "ats_range");
      props.connectForm.change("", "ats_meridian");
    }
  };

  const handleServiceAddressChange = (event, selectedAddress) => {
    if (
      signupClass !== "inside" &&
      !(isOutside && outsideLocationMethod === "municipal")
    ) {
      return;
    }

    const coordinates = getCoordinates(selectedAddress);

    if (!selectedAddress) {
      if (signupClass === "inside") {
        setInsideAddressNeedsGps(false);
        setInsideUsesMapPicker(false);
      }
      props.connectForm.change("", "lat");
      props.connectForm.change("", "lng");
      if (typeof props.setLat === "function") props.setLat(null);
      if (typeof props.setLng === "function") props.setLng(null);
      return;
    }

    if (!coordinates) {
      if (signupClass === "inside") {
        setInsideAddressNeedsGps(true);
        setInsideUsesMapPicker(false);
        setInsideAddressWasTried(true);
      }
      props.connectForm.change("", "lat");
      props.connectForm.change("", "lng");
      if (typeof props.setLat === "function") props.setLat(null);
      if (typeof props.setLng === "function") props.setLng(null);
      return;
    }

    if (signupClass === "inside") {
      setInsideAddressNeedsGps(false);
      setInsideUsesMapPicker(false);
      setInsideAddressWasTried(true);
    }
    props.connectForm.changeMany({
      lat: coordinates.lat.toFixed(6),
      lng: coordinates.lng.toFixed(6)
    });
    if (typeof props.setLat === "function") props.setLat(coordinates.lat);
    if (typeof props.setLng === "function") props.setLng(coordinates.lng);
  };

  const useInsideMapPicker = async () => {
    setInsideAddressNeedsGps(false);
    props.connectForm.change("", "lat");
    props.connectForm.change("", "lng");
    if (typeof props.setLat === "function") props.setLat(null);
    if (typeof props.setLng === "function") props.setLng(null);

    const codebookCoordinates = getCoordinates(selectedArea);
    if (codebookCoordinates) {
      setInsideServiceAreaCoordinates(codebookCoordinates);
      props.connectForm.changeMany({
        lat: codebookCoordinates.lat.toFixed(6),
        lng: codebookCoordinates.lng.toFixed(6)
      });
      if (typeof props.setLat === "function")
        props.setLat(codebookCoordinates.lat);
      if (typeof props.setLng === "function")
        props.setLng(codebookCoordinates.lng);
      setInsideUsesMapPicker(true);
      return;
    }

    if (!selectedArea || !selectedArea.v) {
      setInsideServiceAreaCoordinates(null);
      setInsideUsesMapPicker(true);
      return;
    }

    try {
      const res = await api.get(
        `guest.1/serviceareacoordinates&areaid=${selectedArea.v}`
      );
      const coordinates = getCoordinates(res);
      setInsideServiceAreaCoordinates(coordinates);

      if (!coordinates) {
        setInsideUsesMapPicker(true);
        return;
      }

      props.connectForm.changeMany({
        lat: coordinates.lat.toFixed(6),
        lng: coordinates.lng.toFixed(6)
      });
      if (typeof props.setLat === "function") props.setLat(coordinates.lat);
      if (typeof props.setLng === "function") props.setLng(coordinates.lng);
      setInsideUsesMapPicker(true);
    } catch (e) {
      setInsideServiceAreaCoordinates(null);
      setInsideUsesMapPicker(true);
    }
  };

  const goToContactStep = () => {
    if (!hasLocationInfo) {
      return;
    }

    window.scrollTo(0, 0);
    props.history.push(config.SIGNUP.APP_PATH + "/s/form/contact");
  };

  const focusRecaptcha = () => {
    const recaptcha = document.getElementById("recaptcha");
    if (recaptcha && recaptcha.scrollIntoView) {
      recaptcha.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (recaptcha && recaptcha.focus) {
      recaptcha.focus({ preventScroll: true });
    }
  };

  const getPostponedSignupValues = () => {
    const postponedValues = {
      mailingAddress: "Postponed",
      town: "Postponed",
      regionid: "1",
      postalcode: "T0A 0A0",
      advSrc: "Other (specify)"
    };

    if (accountType !== "p") {
      postponedValues.firstName2 = "Postponed";
      postponedValues.lastName2 = "Postponed";
    }

    return postponedValues;
  };

  const preparePostPayload = payload => {
    const nextPayload = {
      ...payload,
      usingShortForm: true
    };

    if (nextPayload.lat != null && nextPayload.lat !== "") {
      nextPayload.lat = Number(nextPayload.lat);
    }
    if (nextPayload.lng != null && nextPayload.lng !== "") {
      nextPayload.lng = Number(nextPayload.lng);
    }

    if (nextPayload.signupclass === "outside") {
      if (outsideLocationMethod !== "municipal") {
        delete nextPayload.serviceAddress;
      }

      if (outsideLocationMethod !== "legal") {
        ATS_FIELDS.forEach(fieldName => {
          delete nextPayload[fieldName];
        });
      }
    }

    return nextPayload;
  };

  const submitForm = async () => {
    if (submissionInFlightRef.current) return;

    window.scrollTo(0, 0);

    if (!canSeePricing) {
      return;
    }

    if (
      props.needCaptcha &&
      String(props.connectForm.getValue("recaptcha") || "").trim() === ""
    ) {
      setRecaptchaErrorMessage(
        "Please complete the reCAPTCHA check before submitting."
      );
      setTimeout(focusRecaptcha, 0);
      return;
    }

    submissionInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      await props.connectForm.changeMany(getPostponedSignupValues());
      await props.connectForm.changeMany({
        phoneType1: "mo",
        phoneTextme1: "yes"
      });
    } catch (error) {
      submissionInFlightRef.current = false;
      setIsSubmitting(false);
      return;
    }

    postSignupFormWithMetaConversion(props.connectForm.post, preparePostPayload)
      .then(({ response: res, submittedMetaEventId }) => {
        if (!submissionInFlightRef.current) return;

        if (res === false) {
          submissionInFlightRef.current = false;
          setIsSubmitting(false);
          return;
        }
        if (res != null && typeof res === "object") {
          if ("contactid" in res) {
            props.setContactid(res.contactid);
          }
          if ("signupreflink" in res) {
            props.setSignupreflink(res.signupreflink);
          }
          props.setPromoCatalog(
            res.promocatalog &&
              typeof res.promocatalog === "object" &&
              !Array.isArray(res.promocatalog)
              ? res.promocatalog
              : null
          );

          trackMetaSignupFormSubmission(submittedMetaEventId, {
            fn: matchingFirstName,
            ln: matchingLastName,
            ph: matchingPhone
          });
          clearMetaEventId();

          if (gigair) {
            props.history.push(config.SIGNUP.APP_PATH + "/s/fin-gigair");
          } else if (accountType === "p") {
            props.history.push(config.SIGNUP.APP_PATH + "/s/fin-residential");
          } else {
            props.history.push(config.SIGNUP.APP_PATH + "/s/fin-business");
          }
          return;
        }

        submissionInFlightRef.current = false;
        setIsSubmitting(false);
      })
      .catch(error => {
        submissionInFlightRef.current = false;
        setIsSubmitting(false);
        console.error("Signup submit failed:", error);
      });
  };

  const fillMockData = () => {
    const mockEntries = [
      ["p", "accountType"],
      ["Siri Annthony", "firstName1"],
      ["Test", "lastName1"],
      ["4810 50th Ave", "mailingAddress"],
      ["St Paul", "town"],
      ["1", "regionid"],
      ["T0A 3A0", "postalcode"],
      ["test@example.com", "emailAddress"],
      ["(780) 645-4417", "phoneNumber1"],
      ["60012 SEC HWY 881", "serviceAddress"],
      ["Online Advertisement", "advSrc"]
    ];

    const applyNext = index => {
      if (index >= mockEntries.length) {
        setTimeout(() => {
          const submitButton = document.getElementById("submitResidental");
          if (submitButton && submitButton.scrollIntoView) {
            submitButton.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          } else {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: "smooth"
            });
          }
        }, 0);
        return;
      }
      const [value, propName] = mockEntries[index];
      props.connectForm.change(value, propName);
      setTimeout(() => applyNext(index + 1), 0);
    };

    applyNext(0);
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12}>
        {/* hard cap + centering wrapper */}
        <div
          style={{
            width: "min(920px, calc(100vw - 32px))",
            maxWidth: "100%",
            margin: "0 auto"
          }}
        >
          <SignupCard className={classes.formCard} size="xl" maxw={920}>
            {config.IS_LOCAL_ENV ? (
              <Grid item xs={12} className={classes.title}>
                <Grid container justifyContent="flex-end">
                  <CtaButton
                    onClick={fillMockData}
                    color="primary"
                    style={{ textTransform: "none" }}
                  >
                    Fill Mock Data
                  </CtaButton>
                </Grid>
              </Grid>
            ) : null}
            <StepHeader
              step={stepNum}
              title={
                isContactStep
                  ? "Basic information - we'll show your pricing next."
                  : "Tell us what you need and where you need service."
              }
              onBack={onBackFromStepHeader}
            />

            <Grid item xs={12} className={classes.errorBlock}>
              <EasyErrorMessage connectForm={props.connectForm} />
            </Grid>

            <div className={classes.mobilePadding}>
              <div className={classes.formBody}>
                {!isContactStep ? (
                  <>
                    <section className={classes.sectionPanel}>
                      <div className={classes.sectionHeader}>
                        <span className={classes.sectionMarker}>1</span>
                        <h2 className={classes.sectionTitle}>Internet type</h2>
                      </div>
                      <div className={classes.accountChoicePanel}>
                        <EasyRadio
                          propName="accountType"
                          connectForm={props.connectForm}
                        />
                      </div>
                    </section>

                    <section className={classes.sectionPanel}>
                      <div className={classes.sectionHeader}>
                        <span className={classes.sectionMarker}>2</span>
                        <h2 className={classes.sectionTitle}>
                          Physical service location
                        </h2>
                      </div>

                      {props.connectForm.getValue("signupclass") ===
                      "inside" ? (
                        <div className={classes.fieldGroup}>
                          <Typography className={classes.fieldLabel}>
                            Community, lake, or subdivision *
                          </Typography>
                          {selectedArea ? (
                            <div className={classes.serviceAreaDiv}>
                              <div>
                                <p className={classes.serviceAreaTitle}>
                                  {selectedArea.a}
                                </p>
                                <p className={classes.serviceareaname}>
                                  {selectedArea.c || "Service area selected"}
                                </p>
                              </div>
                              <button
                                type="button"
                                className={classes.changeAreaButton}
                                onClick={clearSelectedArea}
                              >
                                Change
                              </button>
                            </div>
                          ) : (
                            <>
                              <TextField
                                id="community-search"
                                placeholder="Search for your location..."
                                variant="outlined"
                                fullWidth
                                onChange={handleAreaInputChange}
                                autoComplete="off"
                                className={classes.searchField}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <SearchIcon />
                                    </InputAdornment>
                                  )
                                }}
                              />
                              <SearchResult
                                selectArea={handleSelectArea}
                                filteredResults={filteredArea}
                              />
                            </>
                          )}
                        </div>
                      ) : null}

                      {isOutside ? (
                        <div className={classes.fieldGroup}>
                          <div className={classes.locationMethodGrid}>
                            <button
                              type="button"
                              className={
                                classes.locationMethodCard +
                                (outsideLocationMethod === "municipal"
                                  ? " " + classes.locationMethodCardSelected
                                  : "")
                              }
                              onClick={() =>
                                chooseOutsideLocationMethod("municipal")
                              }
                            >
                              <span className={classes.locationMethodTitle}>
                                Municipal address
                              </span>
                              <span className={classes.locationMethodText}>
                                Use your rural street address or 911 address.
                              </span>
                            </button>
                            <span className={classes.locationMethodOr}>OR</span>
                            <button
                              type="button"
                              className={
                                classes.locationMethodCard +
                                (outsideLocationMethod === "legal"
                                  ? " " + classes.locationMethodCardSelected
                                  : "")
                              }
                              onClick={() =>
                                chooseOutsideLocationMethod("legal")
                              }
                            >
                              <span className={classes.locationMethodTitle}>
                                Legal land description
                              </span>
                              <span className={classes.locationMethodText}>
                                Use quarter, section, township, range, meridian.
                              </span>
                            </button>
                            <span className={classes.locationMethodOr}>OR</span>
                            <button
                              type="button"
                              className={
                                classes.locationMethodCard +
                                (outsideLocationMethod === "map"
                                  ? " " + classes.locationMethodCardSelected
                                  : "")
                              }
                              onClick={() => chooseOutsideLocationMethod("map")}
                            >
                              <span className={classes.locationMethodTitle}>
                                Find it in a map
                              </span>
                              <span className={classes.locationMethodText}>
                                Use satellite view to pick your property
                                location.
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {showMunicipalAddress &&
                      (!props.connectForm.getValue("signupclass") ||
                        props.connectForm.getValue("signupclass") !==
                          "inside" ||
                        selectedArea) ? (
                        <div className={classes.fieldGroup}>
                          <Typography className={classes.fieldLabel}>
                            {props.connectForm.getValue("signupclass") ===
                            "inside"
                              ? "Physical Address (no PO Box) *"
                              : "Municipal Address *"}
                          </Typography>
                          <div
                            className={
                              classes.addressField + " " + classes.noUnderline
                            }
                          >
                            <EasyAutocomplete
                              connectForm={props.connectForm}
                              propName="serviceAddress"
                              customOptions={options}
                              onInputChange={handleAutoCompleteInputChange}
                              onChange={handleServiceAddressChange}
                              useLabelAsState
                              useAsSolo
                            />
                          </div>
                        </div>
                      ) : null}

                      {showInsideMapLocation ? (
                        <div className={classes.fieldGroup}>
                          <LocationMapPicker
                            classes={classes}
                            connectForm={props.connectForm}
                            initialCenter={insideServiceAreaCoordinates}
                            lat={props.lat}
                            lng={props.lng}
                            setLat={props.setLat}
                            setLng={props.setLng}
                          />
                        </div>
                      ) : null}

                      {showInsideMapFallback ? (
                        <div className={classes.fieldGroup}>
                          <div className={classes.mapFallbackPanel}>
                            <div>
                              <Typography className={classes.mapPickerTitle}>
                                Can't find your exact address?
                              </Typography>
                              <Typography className={classes.mapPickerText}>
                                You can still continue by choosing your service
                                location on the map.
                              </Typography>
                            </div>
                            <button
                              type="button"
                              className={classes.mapFallbackButton}
                              onClick={useInsideMapPicker}
                            >
                              Pick on a map
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {showLegalLandDescription ? (
                        <div
                          key="legallanddescription"
                          className={classes.fieldGroup}
                        >
                          <div className={classes.landGrid}>
                            <div className={classes.landField}>
                              <Typography className={classes.inputTitle}>
                                Quarter *
                              </Typography>
                              <EasyAutocomplete
                                propName={"ats_quarter"}
                                connectForm={props.connectForm}
                              />
                            </div>
                            <div className={classes.landField}>
                              <Typography className={classes.inputTitle}>
                                Section *
                              </Typography>
                              <EasyAutocomplete
                                propName={"ats_section"}
                                connectForm={props.connectForm}
                              />
                            </div>
                            <div className={classes.landField}>
                              <Typography className={classes.inputTitle}>
                                Township *
                              </Typography>
                              <EasyAutocomplete
                                propName={"ats_township"}
                                connectForm={props.connectForm}
                              />
                            </div>
                            <div className={classes.landField}>
                              <Typography className={classes.inputTitle}>
                                Range *
                              </Typography>
                              <EasyAutocomplete
                                propName={"ats_range"}
                                connectForm={props.connectForm}
                              />
                            </div>
                            <div className={classes.landField}>
                              <Typography className={classes.inputTitle}>
                                Meridian *
                              </Typography>
                              <EasyAutocomplete
                                propName={"ats_meridian"}
                                connectForm={props.connectForm}
                              />
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {showMapLocation ? (
                        <div className={classes.fieldGroup}>
                          <LocationMapPicker
                            classes={classes}
                            connectForm={props.connectForm}
                            lat={props.lat}
                            lng={props.lng}
                            setLat={props.setLat}
                            setLng={props.setLng}
                          />
                        </div>
                      ) : null}
                    </section>
                  </>
                ) : (
                  <section className={classes.sectionPanel}>
                    <div className={classes.sectionHeader}>
                      <span className={classes.sectionMarker}>3</span>
                      <h2 className={classes.sectionTitle}>
                        Basic information
                      </h2>
                    </div>
                    <div className={classes.basicInfoStack}>
                      <div className={classes.basicInfoNameRow}>
                        <div className={classes.basicInfoField}>
                          <EasyOpenInput
                            propName="firstName1"
                            connectForm={props.connectForm}
                          />
                        </div>
                        <div className={classes.basicInfoField}>
                          <EasyOpenInput
                            connectForm={props.connectForm}
                            propName="lastName1"
                          />
                        </div>
                      </div>
                      <div className={classes.basicInfoPhoneRowSingle}>
                        <div className={classes.basicInfoField}>
                          <EasyPhoneNumber
                            connectForm={props.connectForm}
                            propName="phoneNumber1"
                            labelText="Mobile Phone Number"
                          />
                        </div>
                        <Typography className={classes.mobileConsentText}>
                          By providing your mobile phone number, you agree that
                          MCSnet may send SMS messages about this inquiry and
                          your service. We use these messages to make the next
                          steps practical, including sending clear instructions
                          for how to continue if you decide MCSnet service is
                          right for you.
                        </Typography>
                      </div>
                    </div>
                  </section>
                )}
              </div>

              {isContactStep && props.needCaptcha ? (
                <SignupRecaptcha
                  key="recaptcha"
                  classes={classes}
                  errorMessage={recaptchaErrorMessage}
                  onChange={response => {
                    props.connectForm.change(response, "recaptcha");
                    if (response) {
                      setRecaptchaErrorMessage("");
                    }
                  }}
                  onError={setRecaptchaErrorMessage}
                />
              ) : null}

              <div className={classes.submitRow}>
                <p className={classes.clearFormText} onClick={clearForm} />
                <div
                  className={
                    classes.submitButtonWrap +
                    (!(isContactStep ? canSeePricing : hasLocationInfo)
                      ? " " + classes.submitButtonWrapDisabled
                      : "")
                  }
                  data-disabled-message="Fill all required fields above before proceeding."
                >
                  <CtaButton
                    id={
                      gigair
                        ? "submitGigair"
                        : props.connectForm.getValue("accountType") === "p"
                        ? "submitResidental"
                        : "submitBusiness"
                    }
                    fullWidth
                    disabled={
                      !(isContactStep ? canSeePricing : hasLocationInfo) ||
                      isSubmitting
                    }
                    onClick={isContactStep ? submitForm : goToContactStep}
                    color="primary"
                  >
                    {isContactStep ? "See Pricing" : "Next"}
                  </CtaButton>
                </div>
              </div>

              <div className={classes.bottomSpacer} />
            </div>
          </SignupCard>
        </div>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(
  loadForm("guest.1/signupform.0", withRouter(Form))
);
